import { useState } from "react";
import { Link } from "react-router-dom";
import { useFireTests } from "@/hooks/useFireTests";
import type { FacetCount, FireTestListParams } from "@/types";
import { apiErrorDetail } from "@/utils/apiError";

/**
 * 참조물질DB '모의현장 DB' 탭 (PLAN 2-11, 2026-10-10).
 *
 * 시료에 '화재시험 조건'이 입력된 자료만 모은다 — 사건 유형·번호로 추정하지
 * 않는다(실제 사건 시료가 참조 화면에 섞이지 않게). 총 개수는 서버 총계다
 * (쪽 길이가 아니다). MS 스펙트럼 수는 저장된 스펙트럼 개수로, RT 구간
 * 평균 스펙트럼도 1건으로 센다 — 원 스캔 수와 다를 수 있다.
 */

const PAGE_SIZE = 50;
const EVAPORATION_LEVELS = [1, 2, 3, 4, 5];

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return value.slice(0, 10).replaceAll("-", ".");
}

function FacetChips({
  label,
  facets,
  selected,
  onSelect,
}: {
  label: string;
  facets: FacetCount[];
  selected: string | undefined;
  onSelect: (value: string | undefined) => void;
}) {
  if (facets.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-gray-500 w-20 shrink-0">
        {label}
      </span>
      {facets.map((facet) => {
        const active = selected === facet.value;
        return (
          <button
            key={facet.value}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(active ? undefined : facet.value)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${
              active
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-gray-300 bg-white text-gray-700 hover:border-blue-400"
            }`}
          >
            {facet.value} {facet.count.toLocaleString("ko-KR")}
          </button>
        );
      })}
    </div>
  );
}

export default function FireTestSampleBrowser() {
  const [skip, setSkip] = useState(0);
  const [substrate, setSubstrate] = useState<string | undefined>();
  const [ignitableLiquid, setIgnitableLiquid] = useState<string | undefined>();
  const [evaporation, setEvaporation] = useState<number | undefined>();
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState<string | undefined>();

  const params: FireTestListParams = {
    skip,
    limit: PAGE_SIZE,
    substrate,
    ignitable_liquid: ignitableLiquid,
    evaporation_level: evaporation,
    q: query,
  };
  const { data, isLoading, isFetching, isPlaceholderData, error } =
    useFireTests(params);

  // 필터가 바뀌면 첫 쪽으로 돌아간다
  const resetTo =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setSkip(0);
    };

  if (isLoading && !data) {
    return (
      <div className="animate-pulse space-y-2 p-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 rounded bg-gray-200" />
        ))}
      </div>
    );
  }
  const filtered =
    !!substrate || !!ignitableLiquid || evaporation != null || !!query;
  const clearFilters = () => {
    setSubstrate(undefined);
    setIgnitableLiquid(undefined);
    setEvaporation(undefined);
    setSearchInput("");
    setQuery(undefined);
    setSkip(0);
  };

  if (error) {
    return (
      <div className="p-12 text-center">
        <p className="mb-2 font-medium text-red-700">
          모의현장 DB 를 불러오지 못했습니다.
        </p>
        {(filtered || skip > 0) && (
          <button
            type="button"
            onClick={clearFilters}
            className="mb-2 rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            필터 초기화
          </button>
        )}
        <p className="text-xs text-gray-500">
          {apiErrorDetail(error, "잠시 뒤 다시 시도하세요.")}
        </p>
      </div>
    );
  }
  if (!data) return null;

  const shownFrom = data.total === 0 ? 0 : skip + 1;
  const shownTo = Math.min(skip + data.items.length, data.total);

  return (
    <div className="p-6 space-y-5">
      {/* 총계 */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="text-2xl font-bold text-gray-900">
              모의현장 시료 {data.total.toLocaleString("ko-KR")}건
            </p>
            <p className="text-lg font-semibold text-gray-700">
              MS 스펙트럼 {data.ms_spectrum_total.toLocaleString("ko-KR")}건
            </p>
            {filtered && (
              <span className="text-xs text-blue-700">필터 적용 중</span>
            )}
            {isFetching && isPlaceholderData && (
              <span className="text-xs text-gray-500">불러오는 중…</span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            시료 화면에서 '화재시험 조건'을 입력한 시료만 모입니다. MS
            스펙트럼은 저장된 스펙트럼 개수이며, RT 구간 평균 스펙트럼도 1건으로
            셉니다.
          </p>
        </div>
      </div>

      {/* 집계·필터 */}
      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <FacetChips
          label="인화성 액체"
          facets={data.facets.ignitable_liquid}
          selected={ignitableLiquid}
          onSelect={resetTo(setIgnitableLiquid)}
        />
        <FacetChips
          label="재질"
          facets={data.facets.substrate}
          selected={substrate}
          onSelect={resetTo(setSubstrate)}
        />
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            증발 단계
            <select
              value={evaporation ?? ""}
              onChange={(e) =>
                resetTo(setEvaporation)(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            >
              <option value="">전체</option>
              {EVAPORATION_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}단계
                </option>
              ))}
            </select>
          </label>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              // 비우면 Enter 없이 바로 검색을 푼다
              if (!e.target.value && query) resetTo(setQuery)(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                resetTo(setQuery)(searchInput.trim() || undefined);
              }
            }}
            aria-label="검색"
            placeholder="시료 ID·시료명·사건 번호 검색"
            className="w-72 rounded border border-gray-300 bg-white px-3 py-1 text-sm"
          />
        </div>
      </div>

      {/* 목록 */}
      {data.items.length === 0 && data.total > 0 ? (
        <div className="p-10 text-center">
          <p className="mb-2 font-medium text-gray-600">
            이 쪽에는 시료가 없습니다.
          </p>
          <button
            type="button"
            onClick={() => setSkip(0)}
            className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            처음으로
          </button>
        </div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center">
          <p className="mb-2 font-medium text-gray-600">
            {filtered
              ? "조건에 맞는 모의현장 시료가 없습니다."
              : "화재시험 조건이 입력된 시료가 없습니다."}
          </p>
          <p className="text-xs text-gray-500">
            시료 상세 화면의 '화재시험 조건'을 입력하면 이 목록에 모입니다.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">시료 ID</th>
                <th className="px-3 py-2 text-left">시료명</th>
                <th className="px-3 py-2 text-left">사건 번호</th>
                <th className="px-3 py-2 text-left">재질</th>
                <th className="px-3 py-2 text-left">인화성 액체</th>
                <th className="px-3 py-2 text-center">증발 단계</th>
                <th className="px-3 py-2 text-center">실험일</th>
                <th className="px-3 py-2 text-right">MS 스펙트럼</th>
                <th className="px-3 py-2 text-center">분석</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/samples/${item.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {item.sample_id}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-gray-900">{item.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {item.case_id != null && item.case_number ? (
                      <Link
                        to={`/cases/${item.case_id}`}
                        className="text-blue-600 hover:text-blue-800"
                        title={item.case_title ?? undefined}
                      >
                        {item.case_number}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {item.fire_experiment.substrate}
                  </td>
                  <td className="px-3 py-2">
                    {item.fire_experiment.ignitable_liquid}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.fire_experiment.evaporation_level}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {formatDate(item.fire_experiment.experiment_date)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {item.ms_spectrum_count.toLocaleString("ko-KR")}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.latest_completed_analysis_id != null ? (
                      <Link
                        to={`/analyses/${item.latest_completed_analysis_id}/dashboard`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        대시보드
                      </Link>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 쪽 */}
      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>
            {shownFrom.toLocaleString("ko-KR")}–
            {shownTo.toLocaleString("ko-KR")} /{" "}
            {data.total.toLocaleString("ko-KR")}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40"
            >
              이전
            </button>
            <button
              type="button"
              disabled={skip + PAGE_SIZE >= data.total}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40"
            >
              다음
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
